import React, { useEffect, useState, useMemo } from 'react';
import ListCard from '../components/ListCard';
import NewClientDialog from '../components/NewClientDialog';
import ClientSelectActions from '../components/ClientSelectActions';
import AppointmentPhones from '../components/AppointmentPhones';
import CalendarFeedDialog from '../components/CalendarFeedDialog';
import { interventionFrom } from '../utils/clientAddress';
import ConfirmationRequestDialog, { ConfirmationBadges, hasSentRequest } from '../components/ConfirmationRequestDialog';
import useMediaQuery, { MOBILE_QUERY } from '../hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  listAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  createReportFromAppointment,
  listCustomers,
} from '../services/api';
import {
  getGoogleAuthUrl,
  getGoogleStatus,
  exchangeGoogleCode,
  disconnectGoogle,
  importGoogleEvents,
  pushGoogleEvents,
} from '../services/googleCalendar';

// The intervention's name: written on the appointment, a legacy on-site
// client record otherwise.
const interventionName = (appt) => appt.contact_name || appt.customer?.name || '';

function getSortValue(appt, key) {
  switch (key) {
    case 'customer':
      return interventionName(appt).toLowerCase();
    case 'company':
      return appt.requesting_company?.name?.toLowerCase() || '';
    case 'title':
      return (appt.title || '').toLowerCase();
    case 'status':
      return (appt.status || '').toLowerCase();
    case 'scheduled_at':
    default:
      return appt.scheduled_at || '';
  }
}

/** One line saying what a push to Google Calendar did. */
function pushSummary(result, t, justConnected) {
  const created = result?.created || 0;
  const failed = result?.failed || 0;
  if (failed > 0) {
    return {
      kind: 'warn',
      text: t('appointments.googlePushPartial', {
        created, failed, reason: (result.errors || []).join(' ; '),
        interpolation: { escapeValue: false },
      }),
    };
  }
  if (created > 0) return { kind: 'ok', text: t('appointments.googlePushed', { count: created }) };
  // Each appointment goes to the calendar of the account that owns it: an
  // account with none upcoming has nothing to send, which is not "all sent".
  if (result?.upcoming === 0) {
    return { kind: 'ok', text: t(justConnected ? 'appointments.googleConnectedNothing' : 'appointments.googleNoneOfYours') };
  }
  return {
    kind: 'ok',
    text: t(justConnected ? 'appointments.googleConnectedNothing' : 'appointments.googleAllSynced'),
  };
}

export default function AppointmentsPage() {
  const { t } = useTranslation();
  const isPhone = useMediaQuery(MOBILE_QUERY);
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('scheduled_at');
  const [sortAsc, setSortAsc] = useState(true);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: '', requesting_company_id: '',
    contact_name: '', address: '', contact_phone: '', contact_email: '', scheduled_at: '', duration_minutes: 60,
    description: '', notes: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Google Calendar
  const [googleConnected, setGoogleConnected] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pushing, setPushing] = useState(false);
  // What the last Google action did — a sync that fails must not be silent.
  const [googleNotice, setGoogleNotice] = useState(null); // { kind: 'ok' | 'warn', text }

  // Appointment whose confirmation dialog is open, and the outcome of the last send.
  const [confirmFor, setConfirmFor] = useState(null);
  const [confirmNotice, setConfirmNotice] = useState('');

  // Entity lists
  const [customers, setCustomers] = useState([]);
  // « Nouveau client » dialog: null, "customer" (intervention) or "company" (facturation).
  const [newClientFor, setNewClientFor] = useState(null);
  // The client the new one starts as a copy of (« Dupliquer »), or null.
  const [newClientSource, setNewClientSource] = useState(null);
  const openNewClient = (sourceClient = null) => {
    setNewClientSource(sourceClient);
    setNewClientFor('company');
  };
  const [feedOpen, setFeedOpen] = useState(false);

  useEffect(() => {
    // One list of clients: both selectors (facturation, intervention) pick from it.
    Promise.all([listAppointments(), listCustomers()])
      .then(([appts, custs]) => {
        setAppointments(appts);
        setCustomers(custs);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    getGoogleStatus()
      .then(setGoogleConnected)
      .catch(() => {});
  }, []);

  const sorted = useMemo(() => {
    const copy = [...appointments];
    copy.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
    return copy;
  }, [appointments, sortKey, sortAsc]);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc((p) => !p);
    else { setSortKey(key); setSortAsc(key !== 'scheduled_at'); }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return ' \u2195';
    return sortAsc ? ' \u2191' : ' \u2193';
  };

  const resetForm = () => {
    setForm({
      title: '', requesting_company_id: '',
      contact_name: '', address: '', contact_phone: '', contact_email: '', scheduled_at: '', duration_minutes: 60,
      description: '', notes: '',
    });
    setEditingId(null);
    setShowForm(false);
    setError('');
    setNewClientFor(null);
  };

  // The billing client is chosen. A private person who pays for their own
  // home is also the intervention: its empty fields take their details.
  const handleBillingChange = (billingId, chosen = null) => {
    setForm((prev) => {
      const previous = customers.find((c) => c.id === prev.requesting_company_id);
      const next = chosen || customers.find((c) => c.id === billingId);
      return {
        ...prev,
        requesting_company_id: billingId,
        ...(next && next.kind === 'individual' ? interventionFrom(prev, previous, next) : {}),
      };
    });
  };

  // A client created from the dialog is the billing client, selected at once.
  const handleClientCreated = (entity, created) => {
    setCustomers((prev) => [...prev, created]);
    handleBillingChange(created.id, created);
    setNewClientFor(null);
  };

  // --- Google Calendar handlers ---
  const handleGoogleConnect = async () => {
    try {
      const url = await getGoogleAuthUrl();
      window.open(url, 'google-auth', 'width=500,height=600');

      // Listen for the authorization code from the popup via postMessage
      const handler = async (event) => {
        // Only accept the OAuth code from our own origin (the callback page is
        // served by the backend on the same origin as the SPA).
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'google-auth-code' && event.data.code) {
          window.removeEventListener('message', handler);
          try {
            const result = await exchangeGoogleCode(event.data.code);
            setGoogleConnected(true);
            // Appointments made before connecting are sent right away.
            const pushed = result?.pushed;
            if (pushed?.created > 0) {
              const refreshed = await listAppointments();
              setAppointments(refreshed);
            }
            setGoogleNotice(pushSummary(pushed, t, true));
          } catch (err) {
            console.error('Google token exchange failed:', err);
          }
        }
      };
      window.addEventListener('message', handler);
    } catch (err) {
      console.error('Google Calendar connection failed:', err);
    }
  };

  const handleGoogleDisconnect = async () => {
    await disconnectGoogle();
    setGoogleConnected(false);
  };

  const handleGooglePush = async () => {
    if (!googleConnected) return;
    setPushing(true);
    setGoogleNotice(null);
    try {
      const result = await pushGoogleEvents();
      if (result.created > 0) setAppointments(await listAppointments());
      setGoogleNotice(pushSummary(result, t, false));
    } catch (err) {
      console.error('Google Calendar push failed:', err);
      setGoogleNotice({ kind: 'warn', text: t('appointments.googlePushFailed') });
    } finally {
      setPushing(false);
    }
  };

  const handleGoogleImport = async () => {
    if (!googleConnected) return;
    setImporting(true);
    try {
      const result = await importGoogleEvents();
      if (result.imported > 0) {
        const refreshed = await listAppointments();
        setAppointments(refreshed);
      }
    } catch (err) {
      console.error('Google Calendar import failed:', err);
    } finally {
      setImporting(false);
    }
  };

  // The booking is saved either way; if Google did not take it, say so.
  const warnIfNotInGoogle = (appt) => {
    if (googleConnected && !appt.google_event_id) {
      setGoogleNotice({ kind: 'warn', text: t('appointments.googleNotSynced') });
    } else {
      setGoogleNotice(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.requesting_company_id) {
      setError(t('appointments.billingRequired'));
      return;
    }
    if (!form.title.trim() || !form.scheduled_at) {
      setError(t('errors.generic'));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingId) {
        const updated = await updateAppointment(editingId, payload);
        setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
        warnIfNotInGoogle(updated);
      } else {
        // Backend auto-syncs to Google Calendar if user is connected
        const created = await createAppointment(payload);
        setAppointments((prev) => [...prev, created]);
        warnIfNotInGoogle(created);
      }
      resetForm();
    } catch {
      setError(t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (appt) => {
    setForm({
      title: appt.title,
      requesting_company_id: appt.requesting_company_id || '',
      contact_name: appt.contact_name || '',
      address: appt.address || '',
      contact_phone: appt.contact_phone || '',
      contact_email: appt.contact_email || '',
      scheduled_at: appt.scheduled_at ? appt.scheduled_at.slice(0, 16) : '',
      duration_minutes: appt.duration_minutes,
      description: appt.description || '',
      notes: appt.notes || '',
    });
    setEditingId(appt.id);
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('appointments.deleteConfirm'))) return;
    // Backend auto-deletes from Google Calendar if linked
    await deleteAppointment(id);
    setAppointments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleCreateReport = async (appt) => {
    try {
      const report = await createReportFromAppointment(appt.id);
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === appt.id ? { ...a, report_id: report.id, status: 'completed' } : a
        )
      );
      navigate(`/reports/${report.id}`);
    } catch {
      // silent
    }
  };

  // The server returns the whole appointment: swapping the row updates the
  // badges and the button label without reloading the list.
  const handleConfirmationSent = (updated, emails) => {
    setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    setConfirmFor(null);
    setConfirmNotice(t('confirmRequest.sentTo', { list: emails.join(', '), interpolation: { escapeValue: false } }));
  };

  const confirmButton = (appt, cls) => (appt.status === 'cancelled' ? null : (
    <button className={`btn ${cls} btn-sm`} onClick={() => { setConfirmNotice(''); setConfirmFor(appt); }}>
      {t(hasSentRequest(appt) ? 'confirmRequest.resend' : 'confirmRequest.button')}
    </button>
  ));

  const handleMarkCompleted = async (appt) => {
    const updated = await updateAppointment(appt.id, { status: 'completed' });
    setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };


  if (loading) return <p style={{ textAlign: 'center', paddingTop: 40 }}>{t('auth.loading')}</p>;

  const statusBadge = (status) => {
    const map = { scheduled: 'badge-draft', completed: 'badge-completed', cancelled: 'badge-cancelled' };
    return <span className={`badge ${map[status] || 'badge-draft'}`}>{t(`appointments.${status}`)}</span>;
  };

  return (
    <div className="fade-in">
      {feedOpen && <CalendarFeedDialog onClose={() => setFeedOpen(false)} />}
      {newClientFor && (
        <NewClientDialog
          entity={newClientFor}
          source={newClientSource}
          customers={customers}
          onCreated={(created) => handleClientCreated(newClientFor, created)}
          onClose={() => setNewClientFor(null)}
        />
      )}
      <div className="page-header">
        <h1 className="page-title">{t('appointments.title')}</h1>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setFeedOpen(true)}>
            {t('calendarFeed.button')}
          </button>
          {googleConnected ? (
            <>
              <button
                className="btn btn-secondary"
                onClick={handleGooglePush}
                disabled={pushing || importing}
              >
                {pushing ? t('auth.loading') : t('appointments.pushGoogle')}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleGoogleImport}
                disabled={importing || pushing}
              >
                {importing ? t('auth.loading') : t('appointments.importGoogle')}
              </button>
              <button className="btn btn-ghost" onClick={handleGoogleDisconnect}>
                {t('appointments.disconnectGoogle')}
              </button>
            </>
          ) : (
            <button className="btn btn-secondary" onClick={handleGoogleConnect}>
              {t('appointments.connectGoogle')}
            </button>
          )}
          {/* While an appointment is being typed, starting another would throw
              it away: the way forward is the form's own button. */}
          <button
            className="btn btn-primary"
            onClick={() => { resetForm(); setShowForm(true); }}
            disabled={showForm}
            title={showForm ? t('appointments.newWhileEditing') : undefined}
            aria-describedby={showForm ? 'appt-in-progress' : undefined}
          >
            + {t('appointments.create')}
          </button>
          {showForm && (
            <span id="appt-in-progress" className="visually-hidden">{t('appointments.newWhileEditing')}</span>
          )}
        </div>
      </div>

      {googleNotice && (
        <div
          className={googleNotice.kind === 'warn' ? 'ai-error' : 'google-notice-ok'}
          role={googleNotice.kind === 'warn' ? 'alert' : 'status'}
          style={{ marginBottom: 16 }}
        >
          {googleNotice.text}
        </div>
      )}

      {confirmNotice && (
        <div className="google-notice-ok" role="status" style={{ marginBottom: 16 }}>{confirmNotice}</div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
            {editingId ? t('appointments.edit') : t('appointments.create')}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label htmlFor="appt-title">{t('appointments.appointmentTitle')} *</label>
                <input
                  id="appt-title"
                  className="form-control"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('appointments.scheduledAt')} *</label>
                <input
                  className="form-control"
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Company selector */}
            <div className="form-group">
              <label htmlFor="appt-company">{t('appointments.company')} *</label>
              <div className="client-select-row">
              <select
                id="appt-company"
                className="form-control"
                value={form.requesting_company_id}
                onChange={(e) => handleBillingChange(e.target.value)}
                required
              >
                <option value="">{t('appointments.selectCompany')}</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
                <ClientSelectActions
                  entity="company"
                  selected={customers.find((c) => c.id === form.requesting_company_id) || null}
                  onNew={() => openNewClient()}
                  onDuplicate={(c) => openNewClient(c)}
                />
              </div>
              <small className="form-hint" style={{ display: 'block', marginTop: 4, color: 'var(--color-text-light)' }}>
                {t('appointments.billingHint')}
              </small>
            </div>

            {/* The intervention, described here: who is on site, where, how to reach them. */}
            <fieldset className="intervention-group">
              <legend>{t('appointments.intervention')}</legend>
              <div className="intervention-grid">
                <div className="form-group">
                  <label htmlFor="appt-contact-name">{t('appointments.contactName')}</label>
                  <input id="appt-contact-name" className="form-control" value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="appt-contact-phone">{t('appointments.contactPhone')}</label>
                  <input id="appt-contact-phone" className="form-control" type="tel" value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
                </div>
                <div className="form-group intervention-wide">
                  <label htmlFor="appt-address">{t('appointments.address')}</label>
                  <input id="appt-address" className="form-control" value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="form-group intervention-wide">
                  <label htmlFor="appt-contact-email">{t('appointments.contactEmail')}</label>
                  <input id="appt-contact-email" className="form-control" type="email" value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                  <small className="form-hint" style={{ display: 'block', marginTop: 4, color: 'var(--color-text-light)' }}>
                    {t('appointments.contactEmailHint')}
                  </small>
                </div>
              </div>
            </fieldset>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
              <div className="form-group">
                <label>{t('appointments.duration')}</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  max="1440"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                  style={{ width: 120 }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('appointments.description')}</label>
              <textarea
                className="form-control"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>{t('appointments.notes')}</label>
              <textarea
                className="form-control"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>

            {error && (
              <p style={{ color: 'var(--color-danger)', marginBottom: 12, fontSize: 13, fontWeight: 500 }}>{error}</p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? t('auth.loading') : (editingId ? t('appointments.save') : t('appointments.createSubmit'))}
              </button>
              <button className="btn btn-secondary" type="button" onClick={resetForm}>
                {t('wizard.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {appointments.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">&#128197;</div>
          <p>{t('appointments.empty')}</p>
        </div>
      ) : isPhone ? (
        <div className="list-cards">
          {sorted.map((appt) => (
            <ListCard
              key={appt.id}
              status={appt.status}
              title={appt.title}
              badge={statusBadge(appt.status)}
              tags={<ConfirmationBadges appt={appt} />}
              meta={[
                `${new Date(appt.scheduled_at).toLocaleDateString()} ${new Date(appt.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
                interventionName(appt),
                appt.requesting_company?.name,
              ]}
              secondary={
                <>
                  {appt.address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appt.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {appt.address}
                    </a>
                  )}
                  <AppointmentPhones appt={appt} />
                </>
              }
              onClick={() => handleEdit(appt)}
              actions={
                <>
                  {!appt.report_id && appt.status !== 'cancelled' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleCreateReport(appt)}>
                      {t('appointments.createReport')}
                    </button>
                  )}
                  {appt.report_id && (
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/reports/${appt.report_id}`)}>
                      {t('appointments.reportExists')}
                    </button>
                  )}
                  {appt.status === 'scheduled' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleMarkCompleted(appt)}>
                      {t('appointments.markCompleted')}
                    </button>
                  )}
                  {confirmButton(appt, 'btn-secondary')}
                </>
              }
              destructive={
                <button
                  className="btn btn-ghost btn-sm"
                  title={t('appointments.delete')}
                  aria-label={t('appointments.delete')}
                  onClick={() => handleDelete(appt.id)}
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              }
            />
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('scheduled_at')}>
                    {t('appointments.scheduledAt')}{sortIcon('scheduled_at')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('title')}>
                    {t('appointments.appointmentTitle')}{sortIcon('title')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('customer')}>
                    {t('appointments.intervention')}{sortIcon('customer')}
                  </th>
                  <th>{t('appointments.address')}</th>
                  <th className="sortable" onClick={() => handleSort('status')}>
                    {t('admin.status')}{sortIcon('status')}
                  </th>
                  <th>{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((appt) => (
                  <tr key={appt.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(appt.scheduled_at).toLocaleDateString()}{' '}
                      <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                        {new Date(appt.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{appt.title}</td>
                    <td style={{ fontSize: 13 }}>
                      {interventionName(appt) || '\u2014'}
                      {appt.requesting_company && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-light)' }}>
                          {appt.requesting_company.name}
                        </div>
                      )}
                      <AppointmentPhones appt={appt} className="appt-phones-table" />
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {appt.address ? (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appt.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--color-accent)' }}
                        >
                          {appt.address}
                        </a>
                      ) : '\u2014'}
                    </td>
                    <td>
                      {statusBadge(appt.status)}
                      <ConfirmationBadges appt={appt} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {appt.status === 'scheduled' && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleMarkCompleted(appt)}>
                            {t('appointments.markCompleted')}
                          </button>
                        )}
                        {!appt.report_id && appt.status !== 'cancelled' && (
                          <button className="btn btn-primary btn-sm" onClick={() => handleCreateReport(appt)}>
                            {t('appointments.createReport')}
                          </button>
                        )}
                        {appt.report_id && (
                          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/reports/${appt.report_id}`)}>
                            {t('appointments.reportExists')}
                          </button>
                        )}
                        {confirmButton(appt, 'btn-secondary')}
                        {appt.google_event_id && (
                          <span style={{ fontSize: 11, color: 'var(--color-text-light)' }} title="Google Calendar">
                            &#128197;
                          </span>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(appt)}>
                          {t('admin.edit')}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(appt.id)}>
                          {t('appointments.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {confirmFor && (
        <ConfirmationRequestDialog
          appointment={confirmFor}
          onSent={handleConfirmationSent}
          onClose={() => setConfirmFor(null)}
        />
      )}
    </div>
  );
}
